import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Bananas from "bananas-commerce-admin";

import {
  GridView as GridViewIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from "@mui/icons-material";

import type {
  DetailedPublicationSchema,
  ObituaryMediaResponse,
  PublishingRuleRetrieveSchema,
} from "@/extensions/mediahouse/types";

import OrdersTab from "./OrdersTab";
import PublishingRulesTab from "./PublishingRulesTab";

import type { Nullish } from "@/utils/types";
export type PublicationDetailTab = "orders" | "publishing-rules";

// Cache publication names outside of React to survive component remounts
const publicationNameCache = new Map<string, string>();

const PublicationDetailPage: Bananas.types.PageComponent = () => {
  const [orders, setOrders] = useState<ObituaryMediaResponse[]>();
  const [schedules, setSchedules] = useState<PublishingRuleRetrieveSchema[]>();
  const [publicationName, setPublicationName] = useState<string | null>(null);
  const publicationId = window.location.pathname.split("/").pop();

  const api = Bananas.useApi();

  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab: Nullish<PublicationDetailTab> =
    (searchParams.get("tab") as PublicationDetailTab) ?? "orders";

  const fetchData = async (id: string) => {
    const operation = api.operations[id];
    if (!operation) {
      throw new Error(`No action found for "${id}"`);
    }
    const response = await operation.call({
      query: { publication: publicationId },
    });
    const data = await response.json();
    return data;
  };

  const refresh = async () => {
    const operation =
      activeTab === "orders"
        ? "mediahouse.obituaries:list"
        : "mediahouse.publishing-rule:list";
    const data = await fetchData(operation);
    if (activeTab === "orders") {
      setOrders(data);
    } else {
      setSchedules(data);
    }
  };

  useEffect(() => {
    if (activeTab === "publishing-rules" && schedules != null) return;
    if (activeTab === "orders" && orders != null) return;
    refresh();
  }, [api, activeTab, orders]);

  useEffect(() => {
    if (!publicationId) return;

    const cached = publicationNameCache.get(publicationId);
    if (cached) {
      setPublicationName(cached);
      return;
    }

    if (orders !== undefined) {
      const order = orders.find(
        (order) => order.publication.abbreviation === publicationId,
      );
      if (order) {
        publicationNameCache.set(publicationId, order.publication.name);
        setPublicationName(order.publication.name);
        return;
      }
    }

    if (schedules !== undefined) {
      for (const schedule of schedules) {
        const publication = schedule.publications.find(
          (pub) => pub.abbreviation === publicationId,
        );
        if (publication) {
          publicationNameCache.set(publicationId, publication.name);
          setPublicationName(publication.name);
          return;
        }
      }
    }

    const fetchPublicationName = async () => {
      try {
        const operation = api.operations["mediahouse.publications:get"];
        if (!operation) {
          throw new Error('Invalid action "mediahouse.publications:get".');
        }
        const response = await operation.call();
        const publications = await response.json();
        const publication = publications.find(
          (pub: DetailedPublicationSchema) =>
            pub.abbreviation === publicationId,
        );
        if (publication) {
          publicationNameCache.set(publicationId, publication.name);
          setPublicationName(publication.name);
        }
      } catch (error) {
        console.error("Failed to set publication title:", error);
      }
    };
    fetchPublicationName();
  }, [api, publicationId, orders, schedules]);

  return (
    <Bananas.Page defaultTab="orders" tabSearchParam="tab">
      <Bananas.Header variant="opaque">
        <Bananas.TitleBar title={publicationName ?? ""}></Bananas.TitleBar>

        <Bananas.Tabs
          aria-label="Kontrollera vilka begravningsärendent som syns."
          defaultValue="orders"
          onChange={(tab) => {
            setSearchParams((sp) => ({
              ...Object.fromEntries(sp.entries()),
              tab: tab,
            }));
          }}
        >
          <Bananas.Tab icon={<GridViewIcon />} label="Ordrar" value="orders" />
          <Bananas.Tab
            icon={<HourglassEmptyIcon />}
            label="Utgivningsschema"
            value="publishing-rules"
          />
        </Bananas.Tabs>
      </Bananas.Header>

      {activeTab === "orders" && <OrdersTab orders={orders ?? []} />}
      {activeTab === "publishing-rules" && (
        <PublishingRulesTab refresh={refresh} schedules={schedules ?? []} />
      )}
    </Bananas.Page>
  );
};

export default PublicationDetailPage;
